import React from 'react';

import styled from '@emotion/styled';
import { sumBy } from 'lodash';

import { COLORS, Text, List } from '@clutter/clean';

import { Spacer } from '@shared/components/helpers';
import { WHOLE_NUMBER_OPTIONS, currency } from '@shared/utils/currency';

import { planUpdateURL } from '@portal/config/routes';
import {
  OrderStatusEnum,
  OrderSubtypeEnum,
  StoragePlanFragment,
  UpcomingSubscriptionDetailsFragment,
  UpcomingSubscriptionSetFragment,
} from '@portal/schema';
import { storageName } from '@portal/utils/plan';

import { AppointmentDetailsOrder } from './appointment_details/full_panel';
import { LineItem, TrackedLinkAction } from './line_item';

const MINIMAL_PROTECTION_PLAN_DESCRIPTION = '-';
const MONTH_TO_MONTH_COMMITMENT = 'Month to month';

/** Challenger (two-tier) catalog: free $1k tier (no minimal-coverage SKU in this funnel) */
const PROTECTION_SLUG_1000_INCLUDED = 'protection_1000_included';

const protectionPlanSlug = (subscription?: UpcomingSubscriptionDetailsFragment | null): string | null => {
  const plan = subscription?.pricing?.plan;
  if (plan && plan.__typename === 'Pricing__ProtectionPlan') {
    return plan.slug;
  }
  return null;
};

/**
 * Legacy: treat as having protection only when monthly charge is positive.
 * Challenger: same, plus included $1k plan (slug) whose subscription total is already $0.
 */
const showProtectionSelected = (
  twoTierProtectionPlanCatalog: boolean,
  subscription?: UpcomingSubscriptionDetailsFragment | null,
): boolean => {
  if (!subscription) return false;
  if (subscription.total > 0) return true;
  if (!twoTierProtectionPlanCatalog) return false;
  return protectionPlanSlug(subscription) === PROTECTION_SLUG_1000_INCLUDED;
};

const PlanTitle = styled(Text.Title)`
  color: ${COLORS.panther};
  margin-bottom: 8px;
`;

const Description = styled(Text.Body)`
  color: ${COLORS.panther};
`;

const TotalCost = styled(Text.Callout)`
  font-weight: 600;
`;

const Container = styled.div`
  margin-bottom: 48px;
`;

const updatePlanTrackingParams = (orderID: string, label: string, objectName: string) => ({
  pageName: 'portal:appointment_details',
  container: 'storage_plan',
  action: 'click',
  objectType: 'link',
  objectName: objectName,
  label: label,
  value: planUpdateURL(),
  order_id: orderID,
});

export const StoragePlan: React.FC<{
  upcomingSubscriptionSet: UpcomingSubscriptionSetFragment;
  term?: number;
  order: AppointmentDetailsOrder;
  twoTierProtectionPlanCatalog: boolean;
}> = ({ upcomingSubscriptionSet, term, order, twoTierProtectionPlanCatalog }) => {
  const { storageSubscriptions, protectionSubscription } = upcomingSubscriptionSet;

  const totalCost = sumBy(storageSubscriptions, ({ total }) => total) + (protectionSubscription?.total || 0);
  const totalDiscount = sumBy(storageSubscriptions, ({ discount }) => discount);
  const totalTax = sumBy(storageSubscriptions, ({ tax }) => tax) + (protectionSubscription?.tax || 0);

  const isLongDistanceMove = !!order.longDistanceMove;
  const showOneMonthFreeStoragePromo = isLongDistanceMove;

  const activeOrder = order.status === OrderStatusEnum.Pending || order.status === OrderStatusEnum.Scheduled;
  const past20DaysAfterScheduled =
    order.longDistanceMove && Date.now() >= new Date(order.scheduled).getTime() + 20 * 24 * 60 * 60 * 1000;

  // This is only visible for pickups that are not yet completed and are not canceled.
  // If a long-distance moving customer has a final return, this should be hidden until 20 days after the pickup has taken place.
  // This is to avoid confusion, since most of these customers will have all items returned before being charged for storage, after the free month is over.
  const visible = isLongDistanceMove
    ? activeOrder &&
      order.longDistanceMove?.delivery.subtype !== OrderSubtypeEnum.Subsequent &&
      !past20DaysAfterScheduled
    : activeOrder;

  if (!visible) {
    return null;
  }

  const protectionSelected = showProtectionSelected(twoTierProtectionPlanCatalog, protectionSubscription);

  return (
    <Container>
      <PlanTitle size="large">Your Storage Plan</PlanTitle>
      <Description>
        You’ll only pay for what you store. If you end up storing more or less than originally planned, we’ll let you
        know.
      </Description>
      <Spacer height="32px" />
      <List borderColor={COLORS.grayBorder}>
        {storageSubscriptions.map((storageSubscription) => (
          <LineItem
            key={storageSubscription.id}
            label={`${storageName(storageSubscription.pricing?.plan as StoragePlanFragment)} storage`}
            action={
              <TrackedLinkAction
                params={{
                  text: 'Edit',
                  url: planUpdateURL(),
                  trackingParams: updatePlanTrackingParams(order.id, 'Edit', 'plan_size'),
                }}
              />
            }
          >
            <div className="flex items-center gap-2">
              {`${currency(storageSubscription.total)}/month`}
              {showOneMonthFreeStoragePromo && <Text.Body>1 Month Free</Text.Body>}
            </div>
          </LineItem>
        ))}
        <LineItem
          label="Protection plan"
          action={
            <TrackedLinkAction
              params={{
                text: protectionSelected ? 'Edit' : 'Add coverage',
                url: planUpdateURL(),
                trackingParams: updatePlanTrackingParams(
                  order.id,
                  protectionSelected ? 'Edit' : 'Add coverage',
                  'item_protection',
                ),
              }}
            />
          }
        >
          {protectionSelected && protectionSubscription
            ? `${currency(protectionSubscription.total, WHOLE_NUMBER_OPTIONS)}/month`
            : MINIMAL_PROTECTION_PLAN_DESCRIPTION}
        </LineItem>
        <LineItem
          label="Term commitment"
          action={
            <TrackedLinkAction
              params={{
                text: 'Edit',
                url: planUpdateURL(),
                trackingParams: updatePlanTrackingParams(order.id, 'Edit', 'commitment'),
              }}
            />
          }
        >
          {term && term > 0 ? `${term} month minimum` : MONTH_TO_MONTH_COMMITMENT}
        </LineItem>
        {!!totalTax && <LineItem label="Taxes">{currency(totalTax)}</LineItem>}
        <LineItem label="Monthly total">
          <TotalCost>{currency(totalCost - totalDiscount + totalTax)}</TotalCost>
        </LineItem>
      </List>
    </Container>
  );
};
